(() => {
  'use strict';

  const normalise = (value) => String(value ?? '')
    .toLowerCase()
    .trim()
    .replace(/&amp;/g, '&')
    .replace(/[^a-z0-9]+/g, '');

  const uniqueBy = (items, getKey) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = getKey(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  class ProductGallery {
    constructor(element) {
      this.element = element;
      this.mode = element.dataset.galleryMode || 'desktop';
      this.track = element.querySelector('[data-selsia-track]');
      this.slides = [...element.querySelectorAll('[data-selsia-slide]')];
      this.thumbs = [...element.querySelectorAll('[data-selsia-thumb]')];
      this.dots = [...element.querySelectorAll('[data-selsia-dot]')];
      this.previousButton = element.querySelector('[data-selsia-prev]');
      this.nextButton = element.querySelector('[data-selsia-next]');
      this.currentNode = element.querySelector('[data-selsia-current]');
      this.totalNode = element.querySelector('[data-selsia-total]');
      this.allowed = [];
      this.allowedKey = '';
      this.currentId = '';
      this.scrollFrame = 0;

      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      element.addEventListener('click', this.onClick);
      element.addEventListener('keydown', this.onKeydown);
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
    }

    setAllowed(mediaIds, preferredId, shouldScroll = false) {
      const normalisedIds = mediaIds.map(String);
      const allowedSet = new Set(normalisedIds);
      const preferred = String(preferredId || '');
      const nextKey = `${normalisedIds.join('|')}::${preferred}`;

      if (nextKey === this.allowedKey && this.allowed.length) return;
      this.allowedKey = nextKey;
      this.allowed = this.slides.filter((slide) => allowedSet.has(String(slide.dataset.mediaId)));

      if (!this.allowed.length && this.slides.length) {
        this.allowed = [this.slides[0]];
        allowedSet.add(String(this.slides[0].dataset.mediaId));
      }

      this.slides.forEach((slide) => {
        slide.dataset.selsiaAllowed = allowedSet.has(String(slide.dataset.mediaId)) ? 'true' : 'false';
      });
      this.thumbs.forEach((thumb) => { thumb.hidden = !allowedSet.has(String(thumb.dataset.mediaId)); });
      this.dots.forEach((dot) => { dot.hidden = !allowedSet.has(String(dot.dataset.mediaId)); });

      const nextId = this.allowed.some((slide) => String(slide.dataset.mediaId) === preferred)
        ? preferred
        : String(this.allowed[0]?.dataset.mediaId || '');

      this.show(nextId, { scroll: shouldScroll, focus: false, behavior: 'auto' });
    }

    show(mediaId, options = {}) {
      if (!this.allowed.length) return;
      const requested = String(mediaId || '');
      const target = this.allowed.find((slide) => String(slide.dataset.mediaId) === requested) || this.allowed[0];
      this.currentId = String(target.dataset.mediaId);

      this.slides.forEach((slide) => {
        const isAllowed = slide.dataset.selsiaAllowed === 'true';
        const isCurrent = String(slide.dataset.mediaId) === this.currentId;
        slide.hidden = this.mode === 'mobile' ? !isAllowed : (!isAllowed || !isCurrent);
        slide.setAttribute('aria-hidden', String(!isAllowed || (this.mode !== 'mobile' && !isCurrent)));
      });

      this.thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === this.currentId;
        thumb.setAttribute('aria-selected', String(active));
        thumb.tabIndex = active ? 0 : -1;
      });
      this.dots.forEach((dot) => dot.setAttribute('aria-current', String(String(dot.dataset.mediaId) === this.currentId)));

      const activeIndex = Math.max(0, this.allowed.findIndex((slide) => String(slide.dataset.mediaId) === this.currentId));
      const total = Math.max(1, this.allowed.length);
      if (this.currentNode) this.currentNode.textContent = String(activeIndex + 1);
      if (this.totalNode) this.totalNode.textContent = String(total);
      if (this.previousButton) this.previousButton.disabled = total <= 1;
      if (this.nextButton) this.nextButton.disabled = total <= 1;

      this.element.closest('[data-selsia-pdp]')
        ?.querySelectorAll('[data-selsia-dialog-open]')
        .forEach((button) => { button.dataset.mediaId = this.currentId; });

      if (this.mode === 'mobile' && options.scroll && this.track) {
        this.track.scrollTo({
          left: target.offsetLeft - this.track.offsetLeft,
          behavior: options.behavior || 'smooth'
        });
      }
      if (options.focus) {
        this.thumbs.find((thumb) => String(thumb.dataset.mediaId) === this.currentId)?.focus();
      }
      this.prioritiseCurrentImage(target);
      this.element.dispatchEvent(new CustomEvent('selsia:gallery-change', {
        bubbles: true,
        detail: { mediaId: this.currentId, mode: this.mode, total, current: activeIndex + 1 }
      }));
    }

    prioritiseCurrentImage(slide) {
      const currentIndex = this.allowed.indexOf(slide);
      const currentImage = slide?.querySelector('img');

      if (currentImage) {
        currentImage.loading = 'eager';
        currentImage.fetchPriority = 'high';
        if (!currentImage.complete) currentImage.decode?.().catch(() => {});
      }

      const warmAdjacent = () => {
        [this.allowed[currentIndex - 1], this.allowed[currentIndex + 1]]
          .filter(Boolean)
          .forEach((item) => {
            const image = item.querySelector('img');
            if (!image || image.complete) return;
            image.loading = 'eager';
            image.fetchPriority = 'low';
          });
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warmAdjacent, { timeout: 300 });
      } else {
        window.setTimeout(warmAdjacent, 40);
      }
    }

    move(step) {
      if (this.allowed.length <= 1) return;
      const currentIndex = this.allowed.findIndex((slide) => String(slide.dataset.mediaId) === this.currentId);
      const nextIndex = (Math.max(0, currentIndex) + step + this.allowed.length) % this.allowed.length;
      this.show(this.allowed[nextIndex].dataset.mediaId, { scroll: this.mode === 'mobile' });
    }

    onClick(event) {
      const thumb = event.target.closest('[data-selsia-thumb]');
      if (thumb && this.element.contains(thumb) && !thumb.hidden) {
        this.show(thumb.dataset.mediaId);
        return;
      }
      const dot = event.target.closest('[data-selsia-dot]');
      if (dot && this.element.contains(dot) && !dot.hidden) {
        this.show(dot.dataset.mediaId, { scroll: true });
        return;
      }
      if (event.target.closest('[data-selsia-prev]')) this.move(-1);
      if (event.target.closest('[data-selsia-next]')) this.move(1);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      }
    }

    onScroll() {
      if (this.mode !== 'mobile' || !this.track || !this.allowed.length) return;
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const pageWidth = this.track.clientWidth || 1;
        const index = Math.max(0, Math.min(this.allowed.length - 1, Math.round(this.track.scrollLeft / pageWidth)));
        const current = this.allowed[index];
        if (current && String(current.dataset.mediaId) !== this.currentId) {
          this.show(current.dataset.mediaId, { scroll: false });
        }
      });
    }

    destroy() {
      cancelAnimationFrame(this.scrollFrame);
      this.element.removeEventListener('click', this.onClick);
      this.element.removeEventListener('keydown', this.onKeydown);
      this.track?.removeEventListener('scroll', this.onScroll);
    }
  }

  class SelsiaPDP {
    constructor(root) {
      this.root = root;
      this.variants = this.readJSON('[data-selsia-variants]');
      this.colorPosition = Math.max(0, Number(root.dataset.colorPosition || 0) - 1);
      this.hasColorOption = Number(root.dataset.colorPosition || 0) > 0;
      this.galleries = [...root.querySelectorAll('[data-selsia-gallery]')].map((gallery) => new ProductGallery(gallery));
      this.dialog = root.querySelector('[data-selsia-media-dialog]');
      this.mediaRecords = this.collectMediaRecords();
      this.optionMeta = this.collectOptionMeta();
      this.setOptionIndex = this.optionMeta.find((option) => normalise(option.name) === 'set')?.index ?? -1;
      // SET products use a different media convention from colour products:
      // one representative/featured image per SET can be clustered at the
      // front of product.media, while the remaining detail media follows.
      // Prefer SET explicitly when the product exposes that option; otherwise
      // retain the existing dynamic gallery-option detection used by colours.
      this.galleryOptionIndex = this.setOptionIndex >= 0 ? this.setOptionIndex : this.detectGalleryOptionIndex();
      this.galleryOptionName = this.optionMeta[this.galleryOptionIndex]?.name || '';
      this.isSetGallery = this.setOptionIndex >= 0 && this.galleryOptionIndex === this.setOptionIndex;
      this.groupCache = new Map();
      this.refreshFrame = 0;

      this.onChange = this.onChange.bind(this);
      this.onVariantEvent = this.onVariantEvent.bind(this);
      this.onRootClick = this.onRootClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onGalleryChange = this.onGalleryChange.bind(this);
      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('variant:change', this.onVariantEvent);
      this.root.addEventListener('variant-change', this.onVariantEvent);
      this.root.addEventListener('product:variant-change', this.onVariantEvent);
      this.root.addEventListener('click', this.onRootClick);
      this.root.addEventListener('selsia:gallery-change', this.onGalleryChange);
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('close', () => this.pauseDialogMedia());

      this.refresh(false);
      this.root.dataset.selsiaReady = 'true';
    }

    readJSON(selector) {
      try {
        return JSON.parse(this.root.querySelector(selector)?.textContent || '[]');
      } catch (error) {
        console.warn('[SELSIA PDP] Invalid product JSON.', error);
        return [];
      }
    }

    collectMediaRecords() {
      const records = [...this.root.querySelectorAll('[data-selsia-slide]')].map((slide) => ({
        id: String(slide.dataset.mediaId || ''),
        alt: String(slide.dataset.mediaAlt || ''),
        position: Number(slide.dataset.mediaPosition || 0)
      }));
      return uniqueBy(records, (record) => record.id).sort((a, b) => a.position - b.position);
    }

    selectedOptions() {
      const options = [];
      const optionCount = Math.max(...this.variants.map((variant) => (variant.options || []).length), 0);

      for (let index = 0; index < optionCount; index += 1) {
        const checked = this.root.querySelector(`.js-variant-item[data-variant-value$="-${index}"]:checked, [data-option-index="${index}"] input[type="radio"]:checked`);
        if (checked) {
          options[index] = checked.value;
          continue;
        }

        const indexedSelect = this.root.querySelector(`select.js-variant-select-item[data-option-index="${index}"]`);
        if (indexedSelect) {
          options[index] = indexedSelect.value;
          continue;
        }

        const selects = [...this.root.querySelectorAll('select.js-variant-select-item, select[name^="options["]')];
        options[index] = selects[index]?.value || '';
      }
      return options;
    }

    selectedVariant() {
      // The checked/select option state is the source of truth. The cart form's
      // hidden variant id can be one event behind while a radio is changing.
      const selected = this.selectedOptions().map(normalise);
      const exact = this.variants.find((variant) => (variant.options || [])
        .every((value, index) => normalise(value) === selected[index]));
      if (exact) return exact;

      const hiddenVariantId = this.variantForms()
        .flatMap((form) => [...form.querySelectorAll('input[name="id"]')])
        .map((input) => String(input.value || ''))
        .find((id) => this.variants.some((variant) => String(variant.id) === id));

      return this.variants.find((variant) => String(variant.id) === hiddenVariantId)
        || this.variants[0]
        || null;
    }

    collectOptionMeta() {
      const count = Math.max(...this.variants.map((variant) => (variant.options || []).length), 0);
      const meta = Array.from({ length: count }, (_, index) => ({ index, name: '' }));

      this.root.querySelectorAll('[data-option-index][data-option-name]').forEach((node) => {
        const index = Number(node.dataset.optionIndex);
        if (!Number.isInteger(index) || index < 0 || index >= count) return;
        if (!meta[index].name) meta[index].name = String(node.dataset.optionName || '').trim();
      });

      return meta;
    }

    optionValues(optionIndex) {
      if (!Number.isInteger(optionIndex) || optionIndex < 0) return [];

      const seen = new Set();
      const values = [];

      this.variants.forEach((variant) => {
        const value = variant.options?.[optionIndex] ?? variant[`option${optionIndex + 1}`];
        const key = normalise(value);
        if (!key || seen.has(key)) return;
        seen.add(key);
        values.push(String(value));
      });

      return values;
    }

    mediaPositionById(mediaId) {
      const id = String(mediaId || '');
      if (!id) return -1;
      return this.mediaRecords.findIndex((record) => String(record.id) === id);
    }

    altMatchesOptionValue(alt, value) {
      const rawAlt = String(alt || '').trim();
      const rawValue = String(value || '').trim();
      if (!rawAlt || !rawValue) return false;

      const altKey = normalise(rawAlt);
      const valueKey = normalise(rawValue);
      if (!altKey || !valueKey) return false;
      if (altKey === valueKey) return true;

      // Compare structured alt-text segments without assuming option names such
      // as Color, Set, Pack, Style, etc.
      const segments = rawAlt
        .split(/[|,;]+/)
        .flatMap((segment) => {
          const pieces = [segment];
          const delimiterIndex = segment.search(/[:=]/);
          if (delimiterIndex >= 0) pieces.push(segment.slice(delimiterIndex + 1));
          return pieces;
        })
        .map((segment) => normalise(segment))
        .filter(Boolean);

      if (segments.includes(valueKey)) return true;

      // Also allow the exact option value as a delimited phrase inside alt text.
      // This avoids false positives such as size "S" matching "Saina".
      const escapeRegExp = (input) => input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const phrase = escapeRegExp(rawValue).replace(/\s+/g, '\\s+');
      const boundary = new RegExp(`(^|[\\s|,;:=()\\[\\]{}\\/_-])${phrase}(?=$|[\\s|,;:=()\\[\\]{}\\/_-])`, 'i');
      return boundary.test(rawAlt);
    }

    altAnchorsForOption(optionIndex) {
      const values = this.optionValues(optionIndex);
      const anchors = new Map();
      if (values.length < 2) return anchors;

      this.mediaRecords.forEach((record, position) => {
        const matches = values.filter((value) => this.altMatchesOptionValue(record.alt, value));

        // Ignore ambiguous media alt text that appears to match multiple values.
        if (matches.length !== 1) return;

        const key = normalise(matches[0]);
        if (!anchors.has(key)) anchors.set(key, position);
      });

      return anchors;
    }

    variantAnchorsForOption(optionIndex) {
      const anchors = new Map();
      if (!Number.isInteger(optionIndex) || optionIndex < 0) return anchors;

      this.variants.forEach((variant) => {
        const value = variant.options?.[optionIndex] ?? variant[`option${optionIndex + 1}`];
        const key = normalise(value);
        if (!key) return;

        const mediaId = variant.featured_media?.id || variant.featured_image?.id;
        const position = this.mediaPositionById(mediaId);
        if (position < 0) return;

        const previous = anchors.get(key);
        if (!Number.isInteger(previous) || position < previous) {
          anchors.set(key, position);
        }
      });

      return anchors;
    }

    galleryBoundariesForOption(optionIndex) {
      const values = this.optionValues(optionIndex);
      const altAnchors = this.altAnchorsForOption(optionIndex);
      const variantAnchors = this.variantAnchorsForOption(optionIndex);
      const boundaries = new Map();

      values.forEach((value) => {
        const key = normalise(value);
        const candidates = [
          altAnchors.get(key),
          variantAnchors.get(key)
        ].filter(Number.isInteger);

        if (candidates.length) boundaries.set(key, Math.min(...candidates));
      });

      return boundaries;
    }

    detectGalleryOptionIndex() {
      const candidates = this.optionMeta.map((option) => {
        const values = this.optionValues(option.index);
        const altAnchors = this.altAnchorsForOption(option.index);
        const variantAnchors = this.variantAnchorsForOption(option.index);
        const boundaries = this.galleryBoundariesForOption(option.index);
        const uniquePositions = new Set([...boundaries.values()]);

        return {
          index: option.index,
          valueCount: values.length,
          boundaryCount: boundaries.size,
          uniquePositionCount: uniquePositions.size,
          altCoverage: altAnchors.size,
          variantCoverage: variantAnchors.size
        };
      }).filter((candidate) => (
        candidate.valueCount > 1
        && candidate.boundaryCount > 1
        && candidate.uniquePositionCount > 1
      ));

      candidates.sort((a, b) => (
        b.uniquePositionCount - a.uniquePositionCount
        || b.altCoverage - a.altCoverage
        || b.boundaryCount - a.boundaryCount
        || b.variantCoverage - a.variantCoverage
        || a.index - b.index
      ));

      return candidates[0]?.index ?? -1;
    }

    selectedGalleryValue() {
      if (this.galleryOptionIndex < 0) return '';
      return this.selectedOptions()[this.galleryOptionIndex]
        || this.selectedVariant()?.options?.[this.galleryOptionIndex]
        || '';
    }

    setRepresentativeMap(optionIndex) {
      const representatives = new Map();
      const values = this.optionValues(optionIndex);
      const valueKeys = new Set(values.map(normalise));

      this.variants.forEach((variant) => {
        const value = variant.options?.[optionIndex] ?? variant[`option${optionIndex + 1}`];
        const key = normalise(value);
        if (!key || !valueKeys.has(key)) return;

        const mediaId = String(variant.featured_media?.id || variant.featured_image?.id || '');
        const position = this.mediaPositionById(mediaId);
        if (!mediaId || position < 0) return;

        const previous = representatives.get(key);
        if (!previous || position < previous.position) {
          representatives.set(key, { id: mediaId, position });
        }
      });

      return representatives;
    }

    setRepresentativeCluster(optionIndex) {
      const values = this.optionValues(optionIndex);
      const representatives = this.setRepresentativeMap(optionIndex);
      if (values.length < 2 || representatives.size !== values.length) return null;

      const ordered = values.map((value) => representatives.get(normalise(value))).filter(Boolean);
      const uniqueIds = new Set(ordered.map((item) => item.id));
      if (uniqueIds.size !== values.length) return null;

      const positions = ordered.map((item) => item.position).sort((a, b) => a - b);
      const min = positions[0];
      const max = positions[positions.length - 1];
      const span = max - min + 1;

      // Representative images for SET products are often stored as a compact
      // run at/near the start of product.media.  These are selectors, not group
      // boundaries.  Detect that shape from the data instead of hard-coding
      // SET names, counts or image counts.
      const compact = span <= values.length + 1;
      const nearStart = min <= values.length;
      if (!compact || !nearStart) return null;

      return { values, representatives, representativeIds: uniqueIds };
    }

    setGroupsFromExplicitAlt(cluster) {
      const groups = new Map();
      const values = cluster.values;
      values.forEach((value) => groups.set(normalise(value), []));

      const representativeIds = cluster.representativeIds;
      let matchedValueCount = 0;

      this.mediaRecords.forEach((record) => {
        if (representativeIds.has(String(record.id))) return;
        const matches = values.filter((value) => this.altMatchesOptionValue(record.alt, value));
        if (matches.length !== 1) return;
        groups.get(normalise(matches[0]))?.push(record.id);
      });

      values.forEach((value) => {
        if ((groups.get(normalise(value)) || []).length) matchedValueCount += 1;
      });

      // Only trust alt grouping when every SET value has at least one detail
      // image. Partial alt coverage is more dangerous than a deterministic
      // media-order fallback.
      if (matchedValueCount !== values.length) return null;
      return groups;
    }

    setGroupsFromMediaOrder(cluster) {
      const values = cluster.values;
      const representativeIds = cluster.representativeIds;
      const detailMedia = this.mediaRecords.filter((record) => !representativeIds.has(String(record.id)));
      const groups = new Map();

      if (!values.length) return groups;

      // Split the *remaining* media in product order across however many SET
      // values Shopify actually provides. Nothing here assumes SET 1..4, a
      // fixed number of images, or equal catalog sizes. The division is derived
      // from the current product data on each page load.
      const base = Math.floor(detailMedia.length / values.length);
      const remainder = detailMedia.length % values.length;
      let cursor = 0;

      values.forEach((value, index) => {
        const size = base + (index < remainder ? 1 : 0);
        const block = detailMedia.slice(cursor, cursor + size).map((record) => record.id);
        cursor += size;
        groups.set(normalise(value), block);
      });

      return groups;
    }

    mediaIdsForSetValue(value) {
      const allMedia = this.mediaRecords.map((record) => record.id);
      const key = normalise(value);
      if (!key || this.setOptionIndex < 0) return allMedia;

      const cacheKey = `set:${this.setOptionIndex}:${key}`;
      if (this.groupCache.has(cacheKey)) return this.groupCache.get(cacheKey);

      const cluster = this.setRepresentativeCluster(this.setOptionIndex);

      if (cluster) {
        const representative = cluster.representatives.get(key);
        if (!representative) return allMedia;

        const detailGroups = this.setGroupsFromExplicitAlt(cluster)
          || this.setGroupsFromMediaOrder(cluster);
        const details = detailGroups.get(key) || [];
        const result = uniqueBy([representative.id, ...details].map(String), (id) => id)
          .filter((id) => this.mediaPositionById(id) >= 0);

        if (result.length) {
          this.groupCache.set(cacheKey, result);
          return result;
        }
      }

      // Some products store each SET as a normal contiguous media block rather
      // than a front-loaded representative cluster. In that case the existing
      // boundary inference is correct and remains the fallback.
      const boundaries = this.galleryBoundariesForOption(this.setOptionIndex);
      const selectedStart = boundaries.get(key);
      const orderedStarts = [...new Set([...boundaries.values()])]
        .filter(Number.isInteger)
        .sort((a, b) => a - b);

      if (Number.isInteger(selectedStart) && orderedStarts.length > 1) {
        const selectedStartIndex = orderedStarts.indexOf(selectedStart);
        if (selectedStartIndex >= 0) {
          const start = selectedStartIndex === 0 ? 0 : selectedStart;
          const end = orderedStarts[selectedStartIndex + 1] ?? this.mediaRecords.length;
          const result = this.mediaRecords.slice(start, end).map((record) => record.id);
          if (result.length) {
            this.groupCache.set(cacheKey, result);
            return result;
          }
        }
      }

      // Last safe fallback: if this is definitely a SET product but Shopify has
      // no usable featured-media/alt markers, partition product.media from the
      // actual SET count. This still avoids collapsing to 1 image or exposing
      // every image for only one SET.
      const values = this.optionValues(this.setOptionIndex);
      const selectedIndex = values.findIndex((item) => normalise(item) === key);
      if (selectedIndex >= 0 && values.length > 1 && this.mediaRecords.length >= values.length) {
        const base = Math.floor(this.mediaRecords.length / values.length);
        const remainder = this.mediaRecords.length % values.length;
        const start = selectedIndex * base + Math.min(selectedIndex, remainder);
        const size = base + (selectedIndex < remainder ? 1 : 0);
        const result = this.mediaRecords.slice(start, start + size).map((record) => record.id);
        if (result.length) {
          this.groupCache.set(cacheKey, result);
          return result;
        }
      }

      return allMedia;
    }

    mediaIdsForGalleryValue(value) {
      if (this.isSetGallery) return this.mediaIdsForSetValue(value);

      const allMedia = this.mediaRecords.map((record) => record.id);

      // Non-SET products retain the previous dynamic behaviour, which keeps
      // existing colour galleries untouched.
      if (this.galleryOptionIndex < 0) return allMedia;

      const key = normalise(value);
      if (!key) return allMedia;

      const cacheKey = `${this.galleryOptionIndex}:${key}`;
      if (this.groupCache.has(cacheKey)) return this.groupCache.get(cacheKey);

      const boundaries = this.galleryBoundariesForOption(this.galleryOptionIndex);
      const selectedStart = boundaries.get(key);
      if (!Number.isInteger(selectedStart)) return allMedia;

      const orderedStarts = [...new Set([...boundaries.values()])]
        .filter(Number.isInteger)
        .sort((a, b) => a - b);
      if (orderedStarts.length < 2) return allMedia;

      const selectedStartIndex = orderedStarts.indexOf(selectedStart);
      if (selectedStartIndex < 0) return allMedia;

      const start = selectedStartIndex === 0 ? 0 : selectedStart;
      const end = orderedStarts[selectedStartIndex + 1] ?? this.mediaRecords.length;
      const result = this.mediaRecords.slice(start, end).map((record) => record.id);

      if (!result.length) return allMedia;
      this.groupCache.set(cacheKey, result);
      return result;
    }

    variantForms() {
      const sectionId = this.root.id;
      return [
        document.getElementById(`productForm-${sectionId}`),
        document.getElementById(`productStickyForm-${sectionId}`),
        document.getElementById(`productFormInstallment-${sectionId}`)
      ].filter(Boolean);
    }

    syncVariantState(variant) {
      const options = this.selectedOptions();

      this.root.querySelectorAll('.js-variant-radio-option-name[data-option-index]').forEach((node) => {
        const index = Number(node.dataset.optionIndex);
        if (Number.isInteger(index) && options[index] != null) node.textContent = options[index];
      });

      if (!variant) return;

      this.variantForms().forEach((form) => {
        form.querySelectorAll('input[name="id"]').forEach((input) => {
          input.value = String(variant.id);
          input.disabled = false;
        });
      });

      this.root.dataset.selectedVariantId = String(variant.id);

      // Keep purchase buttons safe even if the theme's legacy variant component
      // does not fire after the PDP was split into snippets.
      const available = variant.available !== false;
      this.variantForms().forEach((form) => {
        form.querySelectorAll('[name="add"], .js-product-form-submit-btn').forEach((button) => {
          button.disabled = !available;
          button.setAttribute('aria-disabled', String(!available));
        });
      });

      this.root.querySelectorAll('[data-selsia-action-variant-id]').forEach((input) => {
        input.value = String(variant.id);
        input.disabled = false;
      });

      this.root.querySelectorAll('[data-selsia-action-submit]').forEach((button) => {
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
      });

      this.root.querySelectorAll('[data-selsia-action-label]').forEach((label) => {
        label.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      });
    }

    refresh(shouldScroll = false) {
      const variant = this.selectedVariant();
      this.syncVariantState(variant);

      const galleryValue = this.selectedGalleryValue();
      const allowed = this.mediaIdsForGalleryValue(galleryValue);
      const featuredId = String(variant?.featured_media?.id || variant?.featured_image?.id || '');
      const preferred = allowed.map(String).includes(featuredId)
        ? featuredId
        : String(allowed[0] || '');

      this.galleries.forEach((gallery) => gallery.setAllowed(
        allowed,
        preferred,
        shouldScroll && gallery.mode === 'mobile'
      ));
      this.updateDialogCounter();
    }

    scheduleRefresh(shouldScroll = false) {
      this.pendingScroll = this.pendingScroll || shouldScroll;
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => {
        const scroll = Boolean(this.pendingScroll);
        this.pendingScroll = false;
        this.refreshFrame = 0;
        this.refresh(scroll);
      });
    }

    onChange(event) {
      if (!event.target.matches(
        '.js-variant-item, .js-variant-select-item, select[name^="options["], input[type="radio"][name]'
      )) {
        return;
      }

      const optionIndex = Number(
        event.target.dataset.variantValue?.split('-').pop()
        ?? event.target.closest('[data-option-index]')?.dataset.optionIndex
      );

      const galleryOptionChanged =
        Number.isInteger(optionIndex) &&
        optionIndex === this.galleryOptionIndex;

      this.scheduleRefresh(galleryOptionChanged);
    }

    onVariantEvent() {
      this.scheduleRefresh(false);
    }

    onRootClick(event) {
      const colorScrollButton = event.target.closest('[data-selsia-color-scroll]');
      if (colorScrollButton && this.root.contains(colorScrollButton)) {
        event.preventDefault();
        this.scrollColorRail(colorScrollButton);
        return;
      }

      const openButton = event.target.closest('[data-selsia-dialog-open]');
      if (!openButton || !this.dialog) return;
      const dialogGallery = this.galleries.find((gallery) => gallery.mode === 'dialog');
      const sourceGallery = this.galleries.find((gallery) => gallery.mode !== 'dialog' && gallery.currentId === openButton.dataset.mediaId)
        || this.galleries.find((gallery) => gallery.mode !== 'dialog');
      const mediaId = openButton.dataset.mediaId || sourceGallery?.currentId;
      dialogGallery?.show(mediaId);
      this.updateDialogCounter();
      this.dialog.showModal();
      this.dialog.querySelector('[data-selsia-dialog-close]')?.focus();
    }

    onGalleryChange(event) {
      if (event.detail?.mode === 'dialog') this.updateDialogCounter();
    }

    onDialogClick(event) {
      if (event.target.closest('[data-selsia-dialog-close]')) {
        this.dialog.close();
        return;
      }
      if (event.target === this.dialog) this.dialog.close();
      requestAnimationFrame(() => this.updateDialogCounter());
    }

    updateDialogCounter() {
      const gallery = this.galleries.find((item) => item.mode === 'dialog');
      if (!gallery) return;
      const index = Math.max(0, gallery.allowed.findIndex((slide) => String(slide.dataset.mediaId) === gallery.currentId));
      const current = this.dialog?.querySelector('[data-selsia-dialog-current]');
      const total = this.dialog?.querySelector('[data-selsia-dialog-total]');
      if (current) current.textContent = String(index + 1);
      if (total) total.textContent = String(Math.max(1, gallery.allowed.length));
    }

    pauseDialogMedia() {
      this.dialog?.querySelectorAll('video').forEach((video) => video.pause());
      this.dialog?.querySelectorAll('iframe').forEach((iframe) => {
        const source = iframe.src;
        iframe.src = source;
      });
    }

    destroy() {
      this.galleries.forEach((gallery) => gallery.destroy());
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('variant:change', this.onVariantEvent);
      this.root.removeEventListener('variant-change', this.onVariantEvent);
      this.root.removeEventListener('product:variant-change', this.onVariantEvent);
      this.root.removeEventListener('click', this.onRootClick);
      this.root.removeEventListener('selsia:gallery-change', this.onGalleryChange);
      this.dialog?.removeEventListener('click', this.onDialogClick);
    }
  }

  const instances = new WeakMap();

  const mount = (scope = document) => {
    scope.querySelectorAll('[data-selsia-pdp]').forEach((root) => {
      instances.get(root)?.destroy();
      instances.set(root, new SelsiaPDP(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-selsia-pdp]').forEach((root) => instances.get(root)?.destroy());
  });
})();

/* Mobile PDP chrome controller: hides the store-wide mobile dock only on PDP
   pages and leaves the SELSIA wishlist + Add to Cart purchase bar visible. */
(() => {
  'use strict';

  const media = window.matchMedia('(max-width: 768px)');
  const marked = new Set();
  let observer = null;
  let scanFrame = 0;

  const textTokens = ['home', 'shop', 'search', 'account', 'cart'];
  const knownSelectors = [
    '[data-mobile-bottom-nav]',
    '.mobile-bottom-navigation',
    '.mobile-bottom-nav',
    '.bottom-navigation',
    '.bottom-nav',
    '.sticky-bottom-nav',
    '.mobile-nav-bar',
    '.footer-mobile-nav',
    '.mobile-footer-nav',
    '.mobile-navigation-bar'
  ].join(',');

  const isPurchaseBar = (element) => Boolean(
    element.closest?.('[data-selsia-mobile-purchase-bar], .selsia-pdp-sticky-host, .selsia-pdp')
  );

  const looksLikeMobileDock = (element) => {
    if (!(element instanceof HTMLElement) || isPurchaseBar(element)) return false;

    const style = getComputedStyle(element);
    if (!['fixed', 'sticky'].includes(style.position)) return false;

    const rect = element.getBoundingClientRect();
    if (rect.height < 42 || rect.height > 150 || rect.width < window.innerWidth * 0.65) return false;
    if (rect.bottom < window.innerHeight - 12 || rect.top > window.innerHeight) return false;

    const controls = element.querySelectorAll('a, button');
    if (controls.length < 3) return false;

    const text = String(element.textContent || '').toLowerCase();
    const tokenMatches = textTokens.filter((token) => text.includes(token)).length;
    return tokenMatches >= 3;
  };

  const mark = (element) => {
    if (!(element instanceof HTMLElement) || isPurchaseBar(element)) return;
    element.setAttribute('data-selsia-hidden-mobile-nav', 'true');
    marked.add(element);
  };

  const scan = () => {
    cancelAnimationFrame(scanFrame);
    scanFrame = requestAnimationFrame(() => {
      scanFrame = 0;
      const hasPDP = Boolean(document.querySelector('[data-selsia-pdp]'));
      const active = media.matches && hasPDP;
      document.body?.classList.toggle('selsia-pdp-mobile-active', active);

      if (!active) {
        marked.forEach((element) => element.removeAttribute('data-selsia-hidden-mobile-nav'));
        marked.clear();
        return;
      }

      document.querySelectorAll(knownSelectors).forEach(mark);
      document.querySelectorAll('nav, [role="navigation"], footer').forEach((element) => {
        if (looksLikeMobileDock(element)) mark(element);
      });
    });
  };

  const start = () => {
    scan();
    observer?.disconnect();
    observer = new MutationObserver(scan);
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  media.addEventListener?.('change', scan);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', scan);
})();

/* SELSIA purchase actions: local wishlist state + mobile sticky-bar boundary. */
(() => {
  'use strict';

  const WISHLIST_KEY = 'selsia:wishlist:v1';
  const mobile = window.matchMedia('(max-width: 768px)');

  const readWishlist = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch (_) {}
  };

  const productFromButton = (button) => ({
    id: String(button.dataset.productId || ''),
    handle: String(button.dataset.productHandle || ''),
    title: String(button.dataset.productTitle || ''),
    url: String(button.dataset.productUrl || ''),
    image: String(button.dataset.productImage || '')
  });

  const productKey = (item) => item.handle || item.id;

  const syncWishlistButton = (button) => {
    const product = productFromButton(button);
    const key = productKey(product);
    const active = Boolean(key && readWishlist().some((item) => productKey(item) === key));
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${product.title || 'product'} ${active ? 'from' : 'to'} wishlist`);
  };

  const toggleWishlist = (button) => {
    const product = productFromButton(button);
    const key = productKey(product);
    if (!key) return;

    const items = readWishlist();
    const index = items.findIndex((item) => productKey(item) === key);
    let active = false;

    if (index >= 0) {
      items.splice(index, 1);
    } else {
      items.push(product);
      active = true;
    }

    writeWishlist(items);
    syncWishlistButton(button);
    window.dispatchEvent(new CustomEvent('selsia:wishlist-change', { detail: { product, active, items } }));
  };

  const mountWishlist = (scope = document) => {
    scope.querySelectorAll('[data-selsia-product-wishlist]').forEach((button) => {
      if (button.dataset.selsiaWishlistReady === 'true') {
        syncWishlistButton(button);
        return;
      }
      button.dataset.selsiaWishlistReady = 'true';
      syncWishlistButton(button);
      button.addEventListener('click', () => toggleWishlist(button));
    });
  };

  const controllers = new WeakMap();
  const shareHomes = new WeakMap();

  const findShareControl = (root) => root.querySelector('.selsia-pdp__share');

  const rememberShareHome = (share) => {
    if (!share || shareHomes.has(share) || !share.parentNode) return;

    const marker = document.createComment('selsia-pdp-share-home');
    share.parentNode.insertBefore(marker, share);

    shareHomes.set(share, { marker });
  };

  const restoreShareControl = (share) => {
    const home = shareHomes.get(share);
    if (!share || !home?.marker?.parentNode) return;

    home.marker.parentNode.insertBefore(share, home.marker.nextSibling);
    share.classList.remove('selsia-pdp__share--purchase');
  };

  const syncSharePlacement = (root, row) => {
    const share = findShareControl(root);
    if (!share || !row) {
      row?.classList.remove('selsia-actions-has-share');
      return;
    }

    if (!mobile.matches) {
      restoreShareControl(share);
      row.classList.remove('selsia-actions-has-share');
      return;
    }

    rememberShareHome(share);

    /* Move the existing share component itself instead of cloning it.
       Its existing theme share handler/popover stays attached and functional. */
    if (share.parentElement !== row || row.firstElementChild !== share) {
      row.insertBefore(share, row.firstElementChild);
    }

    share.classList.add('selsia-pdp__share--purchase');
    row.classList.add('selsia-actions-has-share');
  };

  const mountStickyBar = (root) => {
    const row = root.querySelector('[data-selsia-zivame-actions-row], [data-selsia-purchase-row]');
    if (!row || controllers.has(root)) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      syncSharePlacement(root, row);

      if (!mobile.matches) {
        row.classList.remove('is-selsia-purchase-hidden');
        return;
      }

      /* Hide before the next section (normally recommendations / related
         products) starts covering the viewport. No recommendation snippet is
         required: the main PDP section boundary is the stop point. */
      const rect = root.getBoundingClientRect();
      const shouldHide = rect.bottom <= window.innerHeight + 10;
      row.classList.toggle('is-selsia-purchase-hidden', shouldHide);
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    mobile.addEventListener?.('change', schedule);
    update();

    const destroy = () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      mobile.removeEventListener?.('change', schedule);

      const share = findShareControl(root);
      if (share) restoreShareControl(share);

      row.classList.remove('selsia-actions-has-share', 'is-selsia-purchase-hidden');
    };

    controllers.set(root, { schedule, row, destroy });
  };

  const mount = (scope = document) => {
    mountWishlist(scope);
    scope.querySelectorAll('[data-selsia-pdp]').forEach(mountStickyBar);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-selsia-pdp]').forEach((root) => {
      const controller = controllers.get(root);
      if (!controller) return;
      controller.destroy?.();
      controllers.delete(root);
    });
  });
})();
/* ==========================================================================
   SELSIA PRODUCT REVIEWS
   - Rating summary scrolls to the review section
   - "See all reviews" opens a right drawer on desktop / full screen on mobile
   - Rating filter + helpful/recent sort work client-side
   ========================================================================== */
(() => {
  'use strict';

  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const scrollToReviews = (trigger) => {
    const selector = trigger.dataset.reviewTarget;
    if (!selector) return;
    const target = document.querySelector(selector);
    if (!target) return;

    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });
  };

  const getReviewRoot = (trigger) => {
    const selector = trigger.dataset.reviewTarget;
    if (selector) return document.querySelector(selector);
    return trigger.closest('[data-selsia-reviews]');
  };

  const drawerHomes = new WeakMap();

  const openDrawer = (root) => {
    const drawer = root?.querySelector('[data-selsia-review-drawer]');
    if (!drawer) return;

    /* Move the drawer to <body> while open.
       This escapes product/section stacking contexts, so Suggested products
       and other sections can never paint over the mobile review drawer. */
    if (drawer.parentNode !== document.body) {
      drawerHomes.set(drawer, {
        parent: drawer.parentNode,
        next: drawer.nextSibling
      });
      document.body.appendChild(drawer);
    }

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('selsia-review-drawer-open');

    requestAnimationFrame(() => {
      drawer.querySelector('[data-selsia-review-close]')?.focus();
    });
  };

  const closeDrawer = (drawer) => {
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('selsia-review-drawer-open');

    const home = drawerHomes.get(drawer);
    if (home?.parent?.isConnected) {
      if (home.next?.parentNode === home.parent) {
        home.parent.insertBefore(drawer, home.next);
      } else {
        home.parent.appendChild(drawer);
      }
      drawerHomes.delete(drawer);
    }
  };

  const refreshReviewList = (drawer) => {
    const list = drawer?.querySelector('[data-selsia-review-list]');
    if (!list) return;

    const filter = drawer.querySelector('[data-selsia-review-filter]')?.value || 'all';
    const sort = drawer.querySelector('[data-selsia-review-sort]')?.value || 'helpful';
    const cards = qsa('[data-selsia-review-card]', list);

    cards.sort((a, b) => {
      if (sort === 'recent') {
        return Number(a.dataset.reviewIndex || 0) - Number(b.dataset.reviewIndex || 0);
      }

      const helpfulDifference =
        Number(b.dataset.helpful || 0) - Number(a.dataset.helpful || 0);

      if (helpfulDifference !== 0) return helpfulDifference;

      return Number(a.dataset.reviewIndex || 0) - Number(b.dataset.reviewIndex || 0);
    });

    cards.forEach((card) => {
      const matches = filter === 'all' || String(card.dataset.rating) === filter;
      card.hidden = !matches;
      list.appendChild(card);
    });
  };

  document.addEventListener('click', (event) => {
    const jump = event.target.closest('[data-selsia-review-jump]');
    if (jump) {
      event.preventDefault();
      scrollToReviews(jump);
      return;
    }

    const open = event.target.closest('[data-selsia-review-open]');
    if (open) {
      const root = getReviewRoot(open);
      openDrawer(root);
      return;
    }

    const close = event.target.closest('[data-selsia-review-close]');
    if (close) {
      closeDrawer(close.closest('[data-selsia-review-drawer]'));
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-selsia-review-filter], [data-selsia-review-sort]')) return;
    refreshReviewList(event.target.closest('[data-selsia-review-drawer]'));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const drawer = document.querySelector('[data-selsia-review-drawer].is-open');
    if (drawer) closeDrawer(drawer);
  });

  const initialiseReviewDrawers = (scope = document) => {
    qsa('[data-selsia-review-drawer]', scope).forEach(refreshReviewList);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseReviewDrawers(), { once: true });
  } else {
    initialiseReviewDrawers();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseReviewDrawers(event.target);
  });
})();
/* ==========================================================================
   SELSIA RATING PLACEMENT
   Creates one responsive rating link from the existing product-specific
   review summary:
   - desktop: right side of price area
   - mobile: floating badge on the product image
   ========================================================================== */
(() => {
  'use strict';

  const PDP = '[data-selsia-pdp]';

  const starMarkup = (fill) => `
    <span class="selsia-review-stars" style="--selsia-review-fill:${fill}%;" aria-hidden="true">
      <span class="selsia-review-stars__base">★★★★★</span>
      <span class="selsia-review-stars__fill">★★★★★</span>
    </span>
  `;

  const installRatingPlacement = (root) => {
    if (!root || root.dataset.selsiaRatingPlacement === 'true') return;

    const source = root.querySelector(
      '.selsia-product-rating-summary .selsia-review-summary[data-review-count]'
    );

    if (!source) return;

    const count = Number(source.dataset.reviewCount || 0);
    const rating = String(source.dataset.reviewRating || '').trim();
    const fill = Number(source.dataset.reviewFill || 0);
    const target = source.dataset.reviewTarget || source.getAttribute('href');

    if (!count || !rating || !target) return;

    const starStyle = source.getAttribute('style') || '';

    /* Desktop: keep the title/price area clean and place Reviews on the right. */
    const price = root.querySelector('.product__prices');
    if (price && !price.querySelector('.selsia-rating-desktop-link')) {
      const desktop = document.createElement('a');
      desktop.className = 'selsia-rating-desktop-link';
      desktop.href = target;
      desktop.dataset.selsiaReviewJump = '';
      desktop.dataset.reviewTarget = target;
      desktop.setAttribute(
        'aria-label',
        `${rating} out of 5, ${count} ratings. Jump to reviews`
      );
      if (starStyle) desktop.setAttribute('style', starStyle);

      desktop.innerHTML = `
        ${starMarkup(fill)}
        <span class="selsia-rating-desktop-link__count">${count} Ratings</span>
        <span class="selsia-rating-desktop-link__divider" aria-hidden="true">|</span>
        <strong class="selsia-rating-desktop-link__reviews">Reviews</strong>
      `;

      price.appendChild(desktop);
    }

    /* Mobile: place a small Clovia-like badge directly over the gallery image. */
    const mobileStage = root.querySelector(
      '.product__media-wrapper--mobile .selsia-gallery__stage'
    );

    if (mobileStage && !mobileStage.querySelector('.selsia-rating-mobile-badge')) {
      const mobile = document.createElement('a');
      mobile.className = 'selsia-rating-mobile-badge';
      mobile.href = target;
      mobile.dataset.selsiaReviewJump = '';
      mobile.dataset.reviewTarget = target;
      mobile.setAttribute(
        'aria-label',
        `${rating} out of 5, ${count} ratings. Jump to reviews`
      );
      if (starStyle) mobile.setAttribute('style', starStyle);

      mobile.innerHTML = `
        <strong class="selsia-rating-mobile-badge__rating">${rating}</strong>
        <span class="selsia-rating-mobile-badge__count">(${count})</span>
        <span class="selsia-rating-mobile-badge__star" aria-hidden="true">★</span>
      `;

      mobileStage.appendChild(mobile);
    }

    root.dataset.selsiaRatingPlacement = 'true';
    root.classList.add('selsia-rating-placement-ready');
  };

  const installAll = () => {
    document.querySelectorAll(PDP).forEach(installRatingPlacement);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installAll, { once: true });
  } else {
    installAll();
  }

  document.addEventListener('shopify:section:load', installAll);
})();

/* ==========================================================
   SELSIA NATIVE PRODUCT SHARE
   Uses the EXISTING theme share button.

   LIVE ANDROID / IPHONE:
   Opens the device's native share sheet.

   SHOPIFY THEME EDITOR / unsupported browser:
   Copies the product link cleanly.

   No extra share button.
   No prompt().
   No duplicate bottom-bar item.
   ========================================================== */

(() => {
  'use strict';

  const SHARE_WRAPPER = '.selsia-pdp__share';
  const PDP_SELECTOR = '[data-selsia-pdp]';


  /* ----------------------------------------------------------
     GET PRODUCT TITLE
     ---------------------------------------------------------- */

  const getProductTitle = (pdp) => {
    return (
      pdp?.querySelector('.product__title h1')
        ?.textContent
        ?.trim() ||

      document
        .querySelector('meta[property="og:title"]')
        ?.getAttribute('content')
        ?.trim() ||

      document.title
    );
  };


  /* ----------------------------------------------------------
     GET CLEAN PRODUCT URL

     Prefer Shopify canonical URL so Theme Editor preview URLs
     such as *.myshopify.com?... are NOT shared.

     Also include the currently selected variant.
     ---------------------------------------------------------- */

  const getProductUrl = (pdp) => {

    const canonical =
      document
        .querySelector('link[rel="canonical"]')
        ?.getAttribute('href');

    let url;

    try {
      url = new URL(
        canonical || window.location.href,
        window.location.origin
      );
    } catch (_) {
      return canonical || window.location.href;
    }


    /* Remove editor / tracking junk */
    [
      'preview_theme_id',
      'pb',
      '_pos',
      '_sid',
      '_ss',
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content'
    ].forEach((parameter) => {
      url.searchParams.delete(parameter);
    });


    /*
     * selsia-pdp.js already stores the selected variant
     * on the PDP root.
     */
    const variantId =
      String(
        pdp?.dataset.selectedVariantId || ''
      ).trim();

    if (variantId) {
      url.searchParams.set(
        'variant',
        variantId
      );
    }


    url.hash = '';

    return url.toString();
  };


  /* ----------------------------------------------------------
     SMALL CLEAN TOAST
     No additional CSS required.
     ---------------------------------------------------------- */

  const showToast = (message) => {

    document
      .querySelectorAll('.selsia-share-toast')
      .forEach((item) => item.remove());


    const toast =
      document.createElement('div');

    toast.className =
      'selsia-share-toast';

    toast.textContent =
      message;


    toast.style.cssText = `
      position: fixed;
      left: 50%;
      bottom: 92px;
      z-index: 2147483647;

      transform: translate(-50%, 12px);

      padding: 11px 17px;

      border-radius: 999px;

      background: #21151a;
      color: #ffffff;

      font-family: inherit;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.2;

      white-space: nowrap;

      box-shadow:
        0 8px 28px rgba(0, 0, 0, .20);

      opacity: 0;

      transition:
        opacity .2s ease,
        transform .2s ease;

      pointer-events: none;
    `;


    document.body.appendChild(toast);


    requestAnimationFrame(() => {

      toast.style.opacity = '1';

      toast.style.transform =
        'translate(-50%, 0)';

    });


    window.setTimeout(() => {

      toast.style.opacity = '0';

      toast.style.transform =
        'translate(-50%, 10px)';


      window.setTimeout(() => {
        toast.remove();
      }, 220);

    }, 1700);

  };


  /* ----------------------------------------------------------
     COPY FALLBACK
     Used only when native sharing is unavailable/blocked.
     ---------------------------------------------------------- */

  const copyProductLink = async (url) => {

    /*
     * Modern clipboard API.
     */
    if (
      navigator.clipboard &&
      window.isSecureContext
    ) {

      try {

        await navigator.clipboard.writeText(
          url
        );

        showToast(
          'Product link copied'
        );

        return;

      } catch (_) {
        /* Continue to legacy fallback */
      }

    }


    /*
     * Fallback for Shopify editor / older browser.
     */
    const textarea =
      document.createElement('textarea');

    textarea.value = url;

    textarea.setAttribute(
      'readonly',
      ''
    );


    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    textarea.style.opacity = '0';


    document.body.appendChild(
      textarea
    );


    textarea.focus();
    textarea.select();


    let copied = false;

    try {

      copied =
        document.execCommand('copy');

    } catch (_) {
      copied = false;
    }


    textarea.remove();


    showToast(
      copied
        ? 'Product link copied'
        : 'Sharing is unavailable in preview'
    );

  };


  /* ----------------------------------------------------------
     NATIVE SHARE
     IMPORTANT:
     navigator.share() is called directly from the click event
     so the browser keeps the required user activation.
     ---------------------------------------------------------- */

  const nativeShare = async (pdp) => {

    const title =
      getProductTitle(pdp);

    const url =
      getProductUrl(pdp);


    const shareData = {
      title: title,
      text: `Check out ${title} on Selsia`,
      url: url
    };


    /*
     * Android Chrome / Samsung Internet /
     * iPhone Safari etc.
     */
    if (
      typeof navigator.share === 'function'
    ) {

      try {

        await navigator.share(
          shareData
        );

        return;

      } catch (error) {

        /*
         * User closed the native share sheet.
         * Do nothing.
         */
        if (
          error?.name === 'AbortError'
        ) {
          return;
        }


        /*
         * Theme Editor iframe or browser policy
         * may reject native sharing.
         */
        console.info(
          '[SELSIA SHARE] Native share unavailable:',
          error
        );

      }

    }


    /*
     * Only reaches here when native sharing
     * isn't available.
     */
    await copyProductLink(url);

  };


  /* ----------------------------------------------------------
     DECORATE EXISTING SHARE COMPONENT

     We DO NOT create another button.
     Your existing share icon remains intact.
     ---------------------------------------------------------- */

  const prepareShareControls = (
    scope = document
  ) => {

    scope
      .querySelectorAll?.(SHARE_WRAPPER)
      .forEach((share) => {

        share.setAttribute(
          'data-selsia-native-share',
          'true'
        );


        /*
         * Close the theme's old share popover,
         * if the component is a <details>.
         */
        if (
          share.matches('details')
        ) {
          share.removeAttribute('open');
        }


        const control =
          share.querySelector('summary') ||
          share.querySelector('button') ||
          share.querySelector('.btn');


        if (control) {

  control.setAttribute(
    'aria-label',
    'Share this product'
  );

  control.setAttribute(
    'title',
    'Share this product'
  );

  /* Zivame-style 3-dot share icon */
  control.innerHTML = `
    <svg
      class="selsia-share-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="18" cy="5" r="2.25"></circle>
      <circle cx="6" cy="12" r="2.25"></circle>
      <circle cx="18" cy="19" r="2.25"></circle>

      <path d="M8 10.9 15.8 6.4"></path>
      <path d="m8 13.1 7.8 4.5"></path>
    </svg>
  `;

}

      });

  };


  /* ----------------------------------------------------------
     CLICK INTERCEPTION

     Capture phase is intentional.

     Your theme already has its own share/popover handler.
     We intercept the PRIMARY share control before that old
     handler opens Facebook/X/etc.
     ---------------------------------------------------------- */

  document.addEventListener(
    'click',
    (event) => {

      const share =
        event.target.closest?.(
          SHARE_WRAPPER
        );

      if (!share) return;


      const pdp =
        share.closest(PDP_SELECTOR);

      if (!pdp) return;


      /*
       * Find the actual main trigger.
       */
      const primaryControl =
        share.querySelector('summary') ||
        share.querySelector('button') ||
        share.querySelector('.btn');


      /*
       * Do not intercept unrelated descendants
       * unless the primary control itself was tapped.
       */
      if (
        primaryControl &&
        !primaryControl.contains(event.target)
      ) {
        return;
      }


      /*
       * Prevent old Shopify/theme share popup.
       */
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();


      if (
        share.matches('details')
      ) {
        share.removeAttribute('open');
      }


      /*
       * MUST stay directly inside this user click.
       */
      nativeShare(pdp);

    },
    true
  );


  /* ----------------------------------------------------------
     INITIALISE
     ---------------------------------------------------------- */

  const initialise = () => {
    prepareShareControls(document);
  };


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialise,
      { once: true }
    );

  } else {

    initialise();

  }


  /*
   * Shopify Theme Editor section reload.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      prepareShareControls(
        event.target
      );
    }
  );

})();

/* ==========================================================
   SELSIA DELIVERY & RETURN DETAILS
   Fixed delivery estimate = current date + 10 calendar days
   ========================================================== */

(() => {
  'use strict';

  const DELIVERY_DAYS = 10;

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };


  const formatDeliveryDate = (date) => {
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      weekday: 'long'
    }).format(date);
  };


  const buildDeliveryBlock = () => {
    const wrapper = document.createElement('div');

    wrapper.className = 'selsia-delivery';
    wrapper.setAttribute('data-selsia-delivery', '');

    wrapper.innerHTML = `
      <h3 class="selsia-delivery__heading">
        Delivery &amp; Return Details
      </h3>

      <div class="selsia-delivery__pincode">
        <span class="selsia-delivery__location" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path
              d="M12 21s7-6.15 7-12A7 7 0 1 0 5 9c0 5.85 7 12 7 12Z"
            ></path>
            <circle cx="12" cy="9" r="2.5"></circle>
          </svg>
        </span>

        <input
          class="selsia-delivery__input"
          type="text"
          inputmode="numeric"
          maxlength="6"
          autocomplete="postal-code"
          placeholder="Enter pincode"
          aria-label="Enter delivery pincode"
        >

        <button
          class="selsia-delivery__check"
          type="button"
        >
          Check
        </button>
      </div>

      <p
        class="selsia-delivery__error"
        hidden
      >
        Please enter a valid 6-digit pincode.
      </p>

      <div
        class="selsia-delivery__result"
        hidden
      >
        <p class="selsia-delivery__estimate">
          Estimated Delivery:
          <strong data-selsia-delivery-date></strong>
        </p>

        <div class="selsia-delivery__benefits">

          <div class="selsia-delivery__benefit">
            <span class="selsia-delivery__benefit-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5z"></path>
                <path d="M4 7.5 12 12l8-4.5"></path>
                <path d="M12 12v9"></path>
                <circle cx="17.5" cy="16.5" r="3.5"></circle>
                <path d="m16.2 16.5 1 1 1.8-2"></path>
              </svg>
            </span>

            <span>
              Cash on<br>
              delivery available
            </span>
          </div>

          <div class="selsia-delivery__benefit">
            <span class="selsia-delivery__benefit-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 4h14v16H5z"></path>
                <path d="M9 4v5l3-2 3 2V4"></path>
                <circle cx="17.5" cy="16.5" r="3.5"></circle>
                <path d="m16 16.5 1.3-1.3"></path>
                <path d="M16 16.5h3"></path>
                <path d="m19 16.5-1.3 1.3"></path>
              </svg>
            </span>

            <span>
              15 days<br>
              Returns &amp; Exchanges
            </span>
          </div>

        </div>
      </div>
    `;

    return wrapper;
  };


  const initialiseDelivery = (pdp) => {
    if (!pdp) return;

    if (pdp.querySelector('[data-selsia-delivery]')) {
      return;
    }

    /*
     * Put delivery immediately AFTER the complete SIZE section.
     */
    const sizeOption =
      pdp.querySelector('.selsia-size-option') ||
      Array.from(
        pdp.querySelectorAll('.product-option[data-option-name]')
      ).find((option) => {
        return String(
          option.dataset.optionName || ''
        ).trim().toLowerCase() === 'size';
      });

    if (!sizeOption) return;


    const delivery = buildDeliveryBlock();

    sizeOption.insertAdjacentElement(
      'afterend',
      delivery
    );


    const input =
      delivery.querySelector('.selsia-delivery__input');

    const button =
      delivery.querySelector('.selsia-delivery__check');

    const result =
      delivery.querySelector('.selsia-delivery__result');

    const error =
      delivery.querySelector('.selsia-delivery__error');

    const dateOutput =
      delivery.querySelector('[data-selsia-delivery-date]');


    let checked = false;


    const reset = () => {
      checked = false;

      input.readOnly = false;
      input.focus();

      button.textContent = 'Check';

      result.hidden = true;
      error.hidden = true;

      delivery.classList.remove(
        'is-checked'
      );
    };


    const checkPincode = () => {
      const pincode =
        input.value.replace(/\D/g, '').slice(0, 6);

      input.value = pincode;

      if (!/^[1-9][0-9]{5}$/.test(pincode)) {
        error.hidden = false;
        result.hidden = true;

        delivery.classList.add(
          'has-error'
        );

        return;
      }

      error.hidden = true;

      delivery.classList.remove(
        'has-error'
      );


      const deliveryDate =
        addDays(
          new Date(),
          DELIVERY_DAYS
        );

      dateOutput.textContent =
        formatDeliveryDate(deliveryDate);


      result.hidden = false;

      input.readOnly = true;

      button.textContent = 'Change';

      delivery.classList.add(
        'is-checked'
      );

      checked = true;
    };


    input.addEventListener('input', () => {
      input.value =
        input.value
          .replace(/\D/g, '')
          .slice(0, 6);

      error.hidden = true;

      delivery.classList.remove(
        'has-error'
      );
    });


    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();

        checkPincode();
      }
    });


    button.addEventListener('click', () => {
      if (checked) {
        reset();
      } else {
        checkPincode();
      }
    });
  };


  const initialiseAllDeliveryBlocks = () => {
    document
      .querySelectorAll('[data-selsia-pdp]')
      .forEach(initialiseDelivery);
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseAllDeliveryBlocks,
      { once: true }
    );
  } else {
    initialiseAllDeliveryBlocks();
  }


  document.addEventListener(
    'shopify:section:load',
    initialiseAllDeliveryBlocks
  );

})();